import { Modal } from '@/shared/components/Modal'
import type { PolicyArtefactType } from '@/lib/apiTypes'
import { usePolicyInputSchema } from '@/lib/hooks'
// The schema walk/render helpers are shared with the editor's completions and hovers, so
// this modal and the suggestion list can never describe the contract differently.
import { resolveRef, typeLabel, type SchemaNode } from '@/features/policies/lib/regoSchema'

const ARTEFACT_LABEL: Record<PolicyArtefactType, string> = {
  module: 'Module', state: 'State', provider: 'Provider', plan: 'Plan',
}

/** Render an object's properties as `path: type — description` rows, recursing one
 *  level into nested objects so the shape is legible without overwhelming. */
function Fields({ node, defs, prefix = '', depth = 0 }: {
  node: SchemaNode; defs?: Record<string, SchemaNode>; prefix?: string; depth?: number
}) {
  let resolved = node
  if (node.$ref) resolved = resolveRef(node.$ref, defs) ?? node
  const props = resolved.properties
  if (!props) return null

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {Object.entries(props).map(([key, raw]) => {
        const child = raw.$ref ? (resolveRef(raw.$ref, defs) ?? raw) : raw
        const path = prefix ? `${prefix}.${key}` : `input.${key}`
        const isObject = (Array.isArray(child.type) ? child.type.includes('object') : child.type === 'object') && child.properties
        return (
          <div key={path} style={{ display: 'grid', gap: 4, paddingLeft: depth * 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <code style={{ color: 'var(--accent)', fontSize: '0.74rem' }}>{path}</code>
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--text3)' }}>{typeLabel(child)}</span>
            </div>
            {child.description && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{child.description}</div>
            )}
            {isObject && depth < 2 && (
              <Fields node={child} defs={defs} prefix={path} depth={depth + 1} />
            )}
          </div>
        )
      })}
    </div>
  )
}

/** (?) helper: the OPA `input` contract for one artefact, pulled from the public
 *  /api/policy-schema endpoint (the source-of-truth JSON Schema). */
export function PolicyInputHelp({ artefactType, onClose }: { artefactType: PolicyArtefactType; onClose: () => void }) {
  const { data: doc, error } = usePolicyInputSchema(artefactType)

  return (
    <Modal track="policy_input_help" title={`${ARTEFACT_LABEL[artefactType]} policy input`} onClose={onClose} maxWidth={640}>
      <div style={{ display: 'grid', gap: 14, fontSize: '0.8rem' }}>
        <div style={{ color: 'var(--text2)' }}>
          Your policy is <code style={{ color: 'var(--accent)' }}>package terramantle.policy</code> and defines a{' '}
          <code style={{ color: 'var(--accent)' }}>deny contains msg</code> set. Return <strong>no</strong> messages to pass;
          return one or more to fail (each message is shown as the failure reason). Below is the{' '}
          <code style={{ color: 'var(--accent)' }}>input</code> document a {ARTEFACT_LABEL[artefactType].toLowerCase()} policy receives.
        </div>

        {error && <div className="feedback-error">Couldn’t load schema: {error}</div>}
        {!doc && !error && <div className="dim">Loading schema…</div>}

        {doc && (
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
            <Fields node={doc} defs={doc.$defs} />
          </div>
        )}

        <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
          Every finding shares <code>severity</code>, <code>rule_id</code>, <code>title</code>, <code>resource</code> and tool-specific <code>meta</code>. The editor
          completes and documents these same paths as you type them (or on <kbd>Ctrl</kbd>+<kbd>Space</kbd>). Full contract at{' '}
          <code style={{ color: 'var(--accent)' }}>/api/policy-schema</code>.
        </div>
      </div>
    </Modal>
  )
}
